import { useState } from 'react';
import { MainLayout } from '@templates/index';
import { Card, Badge, Button, Input, NumberInput, Toast } from '@atoms/index';
import { ConfirmModal } from '@organisms/index';
import { useCourierWalletSummary, useCourierPendingCashPayments, useSubmitCashPayment } from '@/hooks/useFinance';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/currency';

function parseMoneyString(value: string | undefined): number {
  const parsed = Number(value ?? '0');
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function CourierWalletPage() {
  const { data, isLoading, error, refetch } = useCourierWalletSummary();
  const { data: pendingCashData, isLoading: isLoadingPending } = useCourierPendingCashPayments();
  const submitCashMutation = useSubmitCashPayment();

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitAmount, setSubmitAmount] = useState('');
  const [submitNotes, setSubmitNotes] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const totalEarnings = parseMoneyString(data?.totalEarnings);
  const cashOnHand = parseMoneyString(data?.cashOnHand);
  const netBalance = parseMoneyString(data?.netBalance);
  const cashLimit = parseMoneyString(data?.cashBalanceLimit);

  const cashOnHandAbs = Math.max(0, Math.abs(cashOnHand));
  const cashLimitSafe = cashLimit > 0 ? cashLimit : 0;
  const cashRatio = cashLimitSafe > 0 ? Math.min(1, cashOnHandAbs / cashLimitSafe) : 0;
  const isHigh = cashLimitSafe > 0 && cashRatio >= 0.8;

  const handleSubmitCash = async () => {
    if (!submitAmount || parseFloat(submitAmount) <= 0) {
      setToastMessage('Lütfen geçerli bir tutar girin');
      setToastType('error');
      setShowToast(true);
      return;
    }

    if (parseFloat(submitAmount) > cashOnHandAbs) {
      setToastMessage('Teslim edilen tutar borçtan fazla olamaz');
      setToastType('error');
      setShowToast(true);
      return;
    }

    try {
      const result = await submitCashMutation.mutateAsync({
        amount: parseFloat(submitAmount),
        orderIds: selectedOrderIds.length > 0 ? selectedOrderIds : undefined,
        notes: submitNotes || undefined,
      });

      setToastMessage(
        `${formatCurrency(parseFloat(result.submittedAmount))} başarıyla teslim edildi. ${result.closedDebts} borç kapatıldı.`,
      );
      setToastType('success');
      setShowToast(true);
      setShowSubmitModal(false);
      setSubmitAmount('');
      setSubmitNotes('');
      setSelectedOrderIds([]);
      await refetch();
    } catch (error: any) {
      setToastMessage(error?.response?.data?.error?.message || 'Nakit teslim işlemi başarısız oldu');
      setToastType('error');
      setShowToast(true);
    }
  };

  return (
    <MainLayout>
      <div className="py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Cüzdanım</h1>
            <p className="text-gray-600">Kazanç, borç ve limit takibi</p>
          </div>
          {isHigh && <Badge variant="warning">Limit Uyarısı</Badge>}
        </div>

        {isLoading && (
          <Card>
            <div className="py-10 text-center text-gray-600">Yükleniyor...</div>
          </Card>
        )}

        {error && (
          <Card className="border-warning/30 bg-warning/10">
            <div className="text-warning">Cüzdan bilgisi alınamadı.</div>
          </Card>
        )}

        {!isLoading && !error && data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <div className="text-sm text-gray-500 mb-1">Toplam Kazanç</div>
                <div className="text-2xl font-bold text-success">{formatCurrency(totalEarnings)}</div>
              </Card>

              <Card className={cn(isHigh ? 'border-warning bg-warning/5' : 'border-danger/20 bg-danger/5')}>
                <div className="text-sm text-gray-500 mb-1">Cash on Hand (Nakit Borç)</div>
                <div className={cn('text-2xl font-bold', isHigh ? 'text-warning' : 'text-danger')}>
                  {formatCurrency(cashOnHandAbs)}
                </div>
                <div className="text-xs text-gray-500 mt-1">Tahsil edilen nakit platform borcu olarak izlenir.</div>
              </Card>

              <Card>
                <div className="text-sm text-gray-500 mb-1">Net Bakiye</div>
                <div className={cn('text-2xl font-bold', netBalance >= 0 ? 'text-gray-900' : 'text-danger')}>
                  {formatCurrency(netBalance)}
                </div>
              </Card>
            </div>

            <Card>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-bold text-gray-900">Nakit Limit</div>
                  <div className="text-sm text-gray-600">
                    {formatCurrency(cashOnHandAbs)} / {formatCurrency(cashLimitSafe)}
                  </div>
                </div>
                <Badge variant={isHigh ? 'warning' : 'success'}>{Math.round(cashRatio * 100)}%</Badge>
              </div>

              <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className={cn('h-full', isHigh ? 'bg-warning' : 'bg-success')}
                  style={{ width: `${Math.round(cashRatio * 100)}%` }}
                />
              </div>

              {isHigh && (
                <div className="mt-3 rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
                  Lütfen nakitleri en kısa sürede merkeze teslim edin.
                </div>
              )}

              {cashOnHandAbs > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => setShowSubmitModal(true)}
                    className="w-full"
                  >
                    Nakit Teslim Et
                  </Button>
                </div>
              )}
            </Card>

            {pendingCashData && pendingCashData.orders.length > 0 && (
              <Card>
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Bekleyen Nakit Ödemeler</h3>
                  <p className="text-sm text-gray-600">
                    Toplam: {formatCurrency(parseFloat(pendingCashData.totalPendingAmount))}
                  </p>
                </div>

                <div className="space-y-3">
                  {pendingCashData.orders.slice(0, 5).map((order) => (
                    <div
                      key={order.orderId}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{order.orderNumber}</p>
                        <p className="text-sm text-gray-600">
                          {order.deliveredAt
                            ? new Date(order.deliveredAt).toLocaleDateString('tr-TR')
                            : '—'}
                          {' • '}
                          {order.daysSinceDelivery} gün önce teslim edildi
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#E63946]">
                          {formatCurrency(parseFloat(order.debtAmount))}
                        </p>
                        <Badge
                          variant={
                            order.daysSinceDelivery > 7
                              ? 'danger'
                              : order.daysSinceDelivery > 3
                                ? 'warning'
                                : 'success'
                          }
                        >
                          {order.daysSinceDelivery} gün
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {pendingCashData.orders.length > 5 && (
                    <p className="text-sm text-gray-500 text-center">
                      +{pendingCashData.orders.length - 5} sipariş daha
                    </p>
                  )}
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {showSubmitModal && (
        <ConfirmModal
          title="Nakit Teslim Et"
          message={`${formatCurrency(cashOnHandAbs)} borç bulunmaktadır. Teslim edeceğiniz tutarı girin.`}
          confirmText="Teslim Et"
          cancelText="İptal"
          variant="primary"
          onConfirm={handleSubmitCash}
          onCancel={() => {
            setShowSubmitModal(false);
            setSubmitAmount('');
            setSubmitNotes('');
            setSelectedOrderIds([]);
          }}
          isLoading={submitCashMutation.isPending}
        >
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tutar (TL)</label>
              <NumberInput
                value={submitAmount}
                onChange={setSubmitAmount}
                placeholder="0.00"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maksimum: {formatCurrency(cashOnHandAbs)}
              </p>
            </div>

            {pendingCashData && pendingCashData.orders.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Belirli Siparişler İçin Ödeme (Opsiyonel)
                </label>
                <div className="max-h-40 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                  {pendingCashData.orders.map((order) => (
                    <label
                      key={order.orderId}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.includes(order.orderId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrderIds([...selectedOrderIds, order.orderId]);
                          } else {
                            setSelectedOrderIds(selectedOrderIds.filter((id) => id !== order.orderId));
                          }
                        }}
                        className="rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{order.orderNumber}</p>
                        <p className="text-xs text-gray-600">
                          {formatCurrency(parseFloat(order.debtAmount))} • {order.daysSinceDelivery} gün
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notlar (Opsiyonel)
              </label>
              <Input
                type="text"
                value={submitNotes}
                onChange={(e) => setSubmitNotes(e.target.value)}
                placeholder="Örn: Günlük nakit teslimi"
                className="w-full"
              />
            </div>
          </div>
        </ConfirmModal>
      )}

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </MainLayout>
  );
}

